import { Box, Grid2, TextField, Typography } from '@mui/material'
import Button from 'components/Button'
import CountUpPrice from 'components/CountUpPrice'
import { voucherReviewPath } from 'components/Routes/VoucherFlowRoutes'
import DefaultLayout from 'components/layout/DefaultLayout'
import { useFormik } from 'formik'
import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import * as Yup from 'yup'
import useStyles from './styles'

const MIN_DOLLAR_VALUE = 10
const MAX_DOLLAR_VALUE = 1000
const MAX_INPUT_LENGTH = 100

interface FormValues {
  amount: number
  toName: string
  toEmail: string
  note: string
}

const validationSchema = Yup.object().shape({
  amount: Yup.number()
    .min(MIN_DOLLAR_VALUE, `Vouchers can't be less than $${MIN_DOLLAR_VALUE}`)
    .max(
      MAX_DOLLAR_VALUE,
      `Vouchers can't be greater than $${MAX_DOLLAR_VALUE}`
    )
    .required('Required'),
  toName: Yup.string().max(MAX_INPUT_LENGTH, 'Too long').required('Required'),
  toEmail: Yup.string()
    .max(MAX_INPUT_LENGTH, 'Too long')
    .email('Invalid email')
    .required('Required')
})

export default function Gift(): JSX.Element {
  const { classes } = useStyles()

  const navigate = useNavigate()

  const { state } = useLocation()

  const { amount, toEmail, toName, note } = state ?? {}

  const [disableBtn, setDisableBtn] = useState<boolean>(false)

  const onSubmit = async (values: FormValues) => {
    setDisableBtn(true)

    localStorage.removeItem('voucher_form')

    navigate(voucherReviewPath(), {
      state: {
        voucher_amount: values.amount.toString(),
        toEmail: values.toEmail,
        toName: values.toName,
        note: values.note
      }
    })
  }

  const formik = useFormik({
    initialValues: {
      amount: Number(amount) || 10,
      toEmail: toEmail ?? '',
      toName: toName ?? '',
      note: note ?? ''
    },
    validationSchema,
    onSubmit
  })

  const { handleSubmit, handleChange, handleBlur, values, errors, touched } =
    formik

  const renderReviewAndPay = () => {
    return (
      <Grid2>
        <Box component='div'></Box>
        <Button
          onClick={() => handleSubmit()}
          showArrow={false}
          fullWidth
          variant='outlined'
          disabled={disableBtn}
        >
          <Box
            display='flex'
            flexGrow='1'
            justifyContent='space-around'
            flexDirection='row'
          >
            <Typography color='primary'>Review & send gift</Typography>
            <CountUpPrice
              value={values.amount}
              prefix='$'
              duration={0.5}
              variant='body1'
              className={classes.countUp}
            />
          </Box>
        </Button>
      </Grid2>
    )
  }

  return (
    <DefaultLayout heading='Buy gift' showBackIcon>
      <form className={classes.form} onSubmit={handleSubmit}>
        <Grid2 container rowSpacing={2}>
          <Grid2 size={{ xs: 12 }}>
            <TextField
              name='amount'
              label='Amount ($)'
              type='number'
              fullWidth
              required
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.amount}
              helperText={
                Boolean(errors.amount && touched.amount) && errors.amount
              }
              error={Boolean(errors.amount && touched.amount)}
            />
          </Grid2>
          <Grid2 size={{ xs: 12 }}>
            <TextField
              name='toName'
              label='Giftee name'
              fullWidth
              required
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.toName}
              helperText={
                Boolean(errors.toName && touched.toName) &&
                String(errors.toName)
              }
              error={Boolean(errors.toName && touched.toName)}
            />
          </Grid2>
          <Grid2 size={{ xs: 12 }}>
            <TextField
              name='toEmail'
              label='Giftee email'
              fullWidth
              required
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.toEmail}
              helperText={
                Boolean(errors.toEmail && touched.toEmail) &&
                String(errors.toEmail)
              }
              error={Boolean(errors.toEmail && touched.toEmail)}
            />
          </Grid2>
          <Grid2 mb={4} size={{ xs: 12 }}>
            <TextField
              label='Message'
              name='note'
              multiline
              fullWidth
              rows={6}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.note}
            />
          </Grid2>
        </Grid2>
        {renderReviewAndPay()}
      </form>
    </DefaultLayout>
  )
}
